/**
 * `ItemCardSource` over the round-5 api (`specs/M1-B7-item-card-and-updates.md` §5).
 *
 * Like the table view's source it talks to a narrow **port** rather than to the tRPC proxy, so
 * the mapping is unit-testable with a stub and a renamed procedure touches one function.
 */
import type {
  ActivityFacets,
  ActivityQuery,
  CardActivityRow,
  CardAsset,
  CardBoardOption,
  CardGroupOption,
  CardPerson,
  CardSubitem,
  CardUpdate,
  CardUpdateReader,
  ItemCardHeader,
  ItemCardSource,
} from './types';

/** Exactly the procedures the card calls. */
export interface ItemCardApi {
  /**
   * The board block alone (T-6b). The card needs the board's *name*; asking `boards.read` for
   * it dragged in the columns, the groups, their counts, a page query and a cell fetch — 36 ms
   * of the card's 50 on the 84-column board.
   */
  boardHeader(input: { boardId: string }): Promise<{ board: { id: string; name: string } }>;
  boardMembers(input: { boardId: string }): Promise<CardPerson[]>;
  /** `items.byIds` takes `ids`, not `itemIds` — the name matters, the router validates it. */
  itemsByIds(input: { ids: string[] }): Promise<
    { id: string; name: string; boardId: string; groupId: string; parentItemId?: string | null }[]
  >;
  listUpdates(input: { itemId: string; limit: number; pinnedFirst: boolean }): Promise<{
    updates: CardUpdate[];
    cursor: string | null;
  }>;
  updatesCount(input: { itemIds: string[] }): Promise<Record<string, number>>;
  /** P-317: who has seen one update — the named form of the footer's `👁 n`. */
  updateReaders(input: { updateId: string }): Promise<CardUpdateReader[]>;
  listAssets(input: { itemId: string }): Promise<CardAsset[]>;
  listActivity(input: Record<string, unknown>): Promise<{ rows: CardActivityRow[]; total: number }>;
  activityFacets(input: Record<string, unknown>): Promise<ActivityFacets>;
  createUpdate(input: { itemId: string; body: string; mentions: unknown[] }): Promise<CardUpdate>;
  createReply(input: { parentId: string; body: string; mentions: unknown[] }): Promise<CardUpdate>;
  editUpdate(input: { updateId: string; body: string; mentions: unknown[] }): Promise<CardUpdate>;
  deleteUpdate(input: { updateId: string }): Promise<unknown>;
  like(input: { updateId: string }): Promise<unknown>;
  unlike(input: { updateId: string }): Promise<unknown>;
  setPinned(input: { updateId: string; pinned: boolean }): Promise<unknown>;
  /** P-299: the second half of a paste-time upload — link the asset to the posted update. */
  attachAsset(input: { updateId: string; assetId: string }): Promise<unknown>;

  /* ── the card's "…" Options (P-060) — the single-item procedures ─────────── */
  renameItem(input: { itemId: string; name: string }): Promise<unknown>;
  duplicateItem(input: { itemId: string }): Promise<unknown>;
  archiveItem(input: { itemId: string }): Promise<unknown>;
  removeItem(input: { itemId: string }): Promise<unknown>;
  moveItemToGroup(input: { itemId: string; groupId: string }): Promise<unknown>;
  moveItemToBoard(input: { itemId: string; boardId: string; groupId: string }): Promise<unknown>;
  listGroups(input: { boardId: string }): Promise<CardGroupOption[]>;
  listBoards(): Promise<CardBoardOption[]>;

  /* ── P-164 the undo half, P-162 the subitems half ─────────────────────── */
  unarchiveItem(input: { itemId: string; isUndo: boolean }): Promise<unknown>;
  restoreItem(input: { itemId: string; isUndo: boolean }): Promise<unknown>;
  subitemsOf(input: { boardId: string; parentItemIds: string[] }): Promise<{
    boardId: string | null;
    byParent: Record<string, { id: string; name: string }[]>;
  }>;
  createSubitem(input: { parentItemId: string; name: string }): Promise<{ id: string; name: string; boardId: string }>;
}

/** The tRPC proxy, structurally typed so this module never imports server code. */
interface TrpcLike {
  boards: {
    header: { query(input: unknown): Promise<never> };
    members: { query(input: unknown): Promise<never> };
    list: { query(input: unknown): Promise<never> };
  };
  items: {
    byIds: { query(input: unknown): Promise<never> };
    rename: { mutate(input: unknown): Promise<never> };
    duplicate: { mutate(input: unknown): Promise<never> };
    archive: { mutate(input: unknown): Promise<never> };
    remove: { mutate(input: unknown): Promise<never> };
    move: { mutate(input: unknown): Promise<never> };
    moveToBoard: { mutate(input: unknown): Promise<never> };
    unarchive: { mutate(input: unknown): Promise<never> };
    restore: { mutate(input: unknown): Promise<never> };
    subitemsOf: { query(input: unknown): Promise<never> };
    createSubitem: { mutate(input: unknown): Promise<never> };
  };
  updates: {
    listForItem: { query(input: unknown): Promise<never> };
    counts: { query(input: unknown): Promise<never> };
    readers: { query(input: unknown): Promise<never> };
    assets: { query(input: unknown): Promise<never> };
    create: { mutate(input: unknown): Promise<never> };
    reply: { mutate(input: unknown): Promise<never> };
    edit: { mutate(input: unknown): Promise<never> };
    remove: { mutate(input: unknown): Promise<never> };
    like: { mutate(input: unknown): Promise<never> };
    unlike: { mutate(input: unknown): Promise<never> };
    setPinned: { mutate(input: unknown): Promise<never> };
    attachAsset: { mutate(input: unknown): Promise<never> };
  };
  activity: {
    list: { query(input: unknown): Promise<never> };
    facets: { query(input: unknown): Promise<never> };
  };
  /** P-060: the card's own item actions. */
  groups: { list: { query(input: unknown): Promise<never> } };
}

export function createItemCardApi(trpc: TrpcLike): ItemCardApi {
  return {
    boardHeader: (input) => trpc.boards.header.query(input),
    boardMembers: (input) => trpc.boards.members.query({ id: input.boardId }),
    itemsByIds: (input) => trpc.items.byIds.query(input),
    listUpdates: (input) => trpc.updates.listForItem.query(input),
    updatesCount: (input) => trpc.updates.counts.query(input),
    updateReaders: (input) => trpc.updates.readers.query(input),
    listAssets: (input) => trpc.updates.assets.query(input),
    listActivity: (input) => trpc.activity.list.query(input),
    activityFacets: (input) => trpc.activity.facets.query(input),
    createUpdate: (input) => trpc.updates.create.mutate(input),
    createReply: (input) => trpc.updates.reply.mutate(input),
    editUpdate: (input) => trpc.updates.edit.mutate(input),
    deleteUpdate: (input) => trpc.updates.remove.mutate(input),
    like: (input) => trpc.updates.like.mutate(input),
    unlike: (input) => trpc.updates.unlike.mutate(input),
    setPinned: (input) => trpc.updates.setPinned.mutate(input),
    attachAsset: (input) => trpc.updates.attachAsset.mutate(input),
    renameItem: (input) => trpc.items.rename.mutate(input),
    duplicateItem: (input) => trpc.items.duplicate.mutate(input),
    archiveItem: (input) => trpc.items.archive.mutate(input),
    removeItem: (input) => trpc.items.remove.mutate(input),
    moveItemToGroup: (input) => trpc.items.move.mutate(input),
    moveItemToBoard: (input) => trpc.items.moveToBoard.mutate(input),
    listGroups: (input) => trpc.groups.list.query(input),
    listBoards: () => trpc.boards.list.query({}),
    unarchiveItem: (input) => trpc.items.unarchive.mutate(input),
    restoreItem: (input) => trpc.items.restore.mutate(input),
    subitemsOf: (input) => trpc.items.subitemsOf.query(input),
    createSubitem: (input) => trpc.items.createSubitem.mutate(input),
  };
}

export interface TrpcItemCardOptions {
  api: ItemCardApi;
  me: CardPerson | null;
  /** Multipart cannot go through tRPC; the uploader posts to `/files/upload` (B7 §7). */
  uploadEndpoint?: string;
}

/**
 * Fill in whatever a write's answer left out, so every `CardUpdate` in the store has the same
 * shape whether it came from a read, a write or a realtime patch.
 */
function normaliseUpdate(row: CardUpdate): CardUpdate {
  const createdAt = typeof row.createdAt === 'string' ? row.createdAt : new Date(row.createdAt ?? Date.now()).toISOString();
  return {
    ...row,
    id: String(row.id),
    parentId: row.parentId === null || row.parentId === undefined ? null : String(row.parentId),
    createdAt,
    updatedAt: typeof row.updatedAt === 'string' ? row.updatedAt : createdAt,
    editedAt: typeof row.editedAt === 'string' ? row.editedAt : null,
    pinned: row.pinned === true,
    mentions: row.mentions ?? [],
    likes: row.likes ?? [],
    assets: row.assets ?? [],
    replies: row.replies ?? [],
    /* P-029: pass the server's count through when it grows one; `null` keeps the `👁`
       control absent rather than printing a zero nobody counted. */
    viewsCount: typeof row.viewsCount === 'number' ? row.viewsCount : null,
  };
}

export function createTrpcItemCardSource(options: TrpcItemCardOptions): ItemCardSource {
  const { api, me } = options;
  const uploadEndpoint = options.uploadEndpoint ?? '/files/upload';
  let members: CardPerson[] = [];
  /** Learned from the first `header()`; the mention popup needs the board's own people. */
  let boardId: string | null = null;

  return {
    async header(itemId: string): Promise<ItemCardHeader> {
      const [item] = await api.itemsByIds({ ids: [itemId] });
      if (!item) throw new Error(`Item ${itemId} was not found`);
      const [board, counts] = await Promise.all([
        api.boardHeader({ boardId: item.boardId }),
        api.updatesCount({ itemIds: [itemId] }).catch(() => ({}) as Record<string, number>),
      ]);
      // The board is remembered so `mentionCandidates` can ask for the right people.
      boardId = item.boardId;
      return {
        id: item.id,
        name: item.name,
        boardId: item.boardId,
        boardName: board.board.name,
        groupTitle: item.groupId,
        updatesCount: counts[itemId] ?? 0,
        // P-162: a subitem's card shows no subitems section — monday nests one level `[S1§2]`.
        parentItemId: item.parentItemId ?? null,
      };
    },

    async updates(itemId: string) {
      // `pinnedFirst` is the client ordering monday applies; its API does not `[ua:a21]`.
      const page = await api.listUpdates({ itemId, limit: 50, pinnedFirst: true });
      return page.updates;
    },

    assets: (itemId: string) => api.listAssets({ itemId }),

    /*
     * **P-317.** Asked for on the gesture, not hydrated with the page: `update_reads` is a row per
     * (update, reader), and a card with forty updates would otherwise pull forty readerships
     * nobody looked at. The server already answers in read order with the author excluded
     * (P-315), so there is nothing to sort or filter here — only the ids to normalise, as
     * everywhere else in this port.
     */
    async updateReaders({ updateId }): Promise<CardUpdateReader[]> {
      const rows = await api.updateReaders({ updateId });
      return (rows ?? []).map((row) => ({
        id: String(row.id),
        name: row.name,
        photoUrl: row.photoUrl ?? null,
        readAt: row.readAt,
      }));
    },

    activity: (query: ActivityQuery) =>
      api.listActivity({
        boardId: Number(query.boardId),
        ...(query.itemIds ? { itemIds: query.itemIds.map(Number) } : {}),
        ...(query.userIds ? { userIds: query.userIds.map(Number) } : {}),
        ...(query.columnIds ? { columnIds: query.columnIds } : {}),
        ...(query.groupIds ? { groupIds: query.groupIds } : {}),
        ...(query.events ? { events: query.events } : {}),
        ...(query.from ? { from: query.from } : {}),
        limit: query.limit ?? 50,
        page: query.page ?? 1,
      }),

    facets: (query: ActivityQuery) =>
      api.activityFacets({
        boardId: Number(query.boardId),
        ...(query.groupIds ? { groupIds: query.groupIds } : {}),
        ...(query.columnIds ? { columnIds: query.columnIds } : {}),
        ...(query.from ? { from: query.from } : {}),
        limit: 100,
      }),

    me: () => me,

    async mentionCandidates(query: string) {
      if (members.length === 0 && boardId !== null) {
        // The board's own people; the popup narrows locally so typing does not round-trip.
        members = await api.boardMembers({ boardId }).catch(() => []);
      }
      const term = query.trim().toLowerCase();
      return members.filter((person) => person.name.toLowerCase().includes(term)).slice(0, 8);
    },

    /*
     * **The three writers return the raw `updates` row, not an `UpdateView`** — no `replies`,
     * no `likes`, no `assets`, `createdAt` as whatever JSON made of a `Date`. Dropping that
     * into the card's tree crashed the whole screen on the *next* realtime event, because
     * `existsInTree` walks `entry.replies` (round 6g, found by `item-card-realtime.spec.ts`
     * B7-A12, whose error boundary hid the cause until the spec started logging `pageerror`).
     * Normalising here is the port's job: the card's own shape is this file's contract.
     */
    createUpdate: async ({ itemId, body, mentions }) =>
      normaliseUpdate(await api.createUpdate({ itemId, body, mentions })),
    createReply: async ({ parentId, body, mentions }) =>
      normaliseUpdate(await api.createReply({ parentId, body, mentions })),
    editUpdate: async ({ updateId, body, mentions }) =>
      normaliseUpdate(await api.editUpdate({ updateId, body, mentions })),
    deleteUpdate: async ({ updateId }) => {
      await api.deleteUpdate({ updateId });
    },
    setLiked: async ({ updateId, liked }) => {
      await (liked ? api.like({ updateId }) : api.unlike({ updateId }));
    },
    setPinned: async ({ updateId, pinned }) => {
      await api.setPinned({ updateId, pinned });
    },

    /* P-299: `updates.attachAsset`, the router over the same `attachAssetToUpdate` service the
       file route calls when an upload *could* name its update (B7 §7). */
    attachAsset: async ({ updateId, assetId }) => {
      await api.attachAsset({ updateId, assetId });
    },

    /*
     * **P-164 — the undo half.** `isUndo: true` is what the service writes as
     * `is_undo_action` on the activity row `[ba§2]`, so an Undo taken from the card is the same
     * event as an Undo taken from the grid's toast.
     */
    unarchiveItem: async ({ itemId }) => {
      await api.unarchiveItem({ itemId, isUndo: true });
    },
    restoreItem: async ({ itemId }) => {
      await api.restoreItem({ itemId, isUndo: true });
    },

    /*
     * **P-162 — the subitems surface.** `items.subitemsOf` is the same call the grid's nested
     * rows make, and it answers for a *page* of parents: the card asks for exactly one, so the
     * answer is `byParent[itemId]`. A board that has never had a subitem answers `boardId:
     * null` `[sub§2]` — an empty list, not an error.
     */
    async subitemsOf({ boardId, itemId }): Promise<CardSubitem[]> {
      const read = await api.subitemsOf({ boardId, parentItemIds: [itemId] });
      if (!read || read.boardId === null) return [];
      const rows = read.byParent?.[itemId] ?? [];
      return rows.map((row) => ({ id: String(row.id), name: row.name, boardId: String(read.boardId) }));
    },

    async createSubitem({ parentItemId, name }): Promise<CardSubitem> {
      const created = await api.createSubitem({ parentItemId, name });
      return { id: String(created.id), name: created.name, boardId: String(created.boardId) };
    },

    async uploadFile({ itemId, updateId, file }) {
      const form = new FormData();
      form.append('item_id', itemId);
      if (updateId) form.append('update_id', updateId);
      form.append('file', file, file.name);
      const response = await fetch(uploadEndpoint, {
        method: 'POST',
        body: form,
        credentials: 'same-origin',
        // The CSRF guard every state-changing browser call carries (M0-F3 §5.3).
        headers: { 'x-elizmonday-client': 'web' },
      });
      /*
       * **The api's own sentence, not ours** (P-299). `/files/upload` answers a refusal as
       * `{ error: "<sentence>" }` — `File exceeds the 500 MB limit`, `Empty file`,
       * `Not authenticated`, the view-only refusal — and the composer puts exactly that in its
       * toast. Throwing `Upload failed with 400` instead, as this did, threw the sentence away
       * at the one moment the person needed it.
       */
      if (!response.ok) {
        const said = await response
          .clone()
          .json()
          .then((body: { error?: unknown; message?: unknown }) => {
            const sentence = typeof body.error === 'string' ? body.error : body.message;
            return typeof sentence === 'string' && sentence.trim() !== '' ? sentence : null;
          })
          .catch(() => null);
        throw new Error(said ?? `Upload failed with ${String(response.status)}`);
      }
      return (await response.json()) as CardAsset;
    },

    /* ── the card's "…" Options (P-060) ─────────────────────────────────────
     *
     * Every one of these is the **single-item** procedure — `items.rename`, `items.duplicate`,
     * `items.archive`, `items.remove`, `items.move`, `items.moveToBoard` — so an action taken
     * on the card writes monday's single-item activity vocabulary and not a batch row.
     */
    renameItem: async ({ itemId, name }) => void (await api.renameItem({ itemId, name })),
    duplicateItem: async ({ itemId }) => void (await api.duplicateItem({ itemId })),
    archiveItem: async ({ itemId }) => void (await api.archiveItem({ itemId })),
    deleteItem: async ({ itemId }) => void (await api.removeItem({ itemId })),
    moveItemToGroup: async ({ itemId, groupId }) =>
      void (await api.moveItemToGroup({ itemId, groupId })),
    moveItemToBoard: async (input) => void (await api.moveItemToBoard(input)),

    async boardGroups({ boardId: forBoard }) {
      const rows = await api.listGroups({ boardId: forBoard });
      return rows.map((group) => ({ id: String(group.id), title: group.title }));
    },

    async boards() {
      const rows = await api.listBoards();
      return rows.map((board) => ({ id: String(board.id), name: board.name }));
    },
  };
}
